/**
 * Static taxonomy data for expense categorization dropdowns.
 *
 * Combined from both music (performing arts) and cinema (audiovisual) sector
 * taxonomies. For categories that appear in both sectors (produccion_tecnica,
 * contingencias), subcategories are merged and deduplicated.
 *
 * Source: services/classifier/app/prompts/taxonomy/music.json
 *         services/classifier/app/prompts/taxonomy/cinema.json
 */

import type { FilterOption, OptionGroup } from "@/domain/types";

// ── Types ───────────────────────────────────────────────────────────

interface TaxonomySubcategory {
	id: string;
	name: string;
}

interface TaxonomyCategory {
	id: string;
	name: string;
	sector: "music" | "cinema" | "shared";
	subcategories: TaxonomySubcategory[];
}

// ── Data ────────────────────────────────────────────────────────────

export const TAXONOMY_CATEGORIES: TaxonomyCategory[] = [
	// ── Music categories ────────────────────────────────────────────
	{
		id: "talento_artistico",
		name: "Talento Artístico",
		sector: "music",
		subcategories: [
			{ id: "cache_artista", name: "Caché del artista" },
			{ id: "booking_fee", name: "Booking y agencia artística" },
			{ id: "backstage", name: "Servicios backstage" },
			{ id: "camerinos", name: "Camerinos" },
			{ id: "derechos_autor", name: "Derechos de autor" },
		],
	},
	{
		id: "produccion_artistica",
		name: "Producción Artística",
		sector: "music",
		subcategories: [
			{ id: "desplazamiento_artistas", name: "Desplazamiento artistas" },
			{ id: "alojamiento_artistas", name: "Alojamiento artistas" },
			{ id: "catering_artistas", name: "Catering y dietas artistas" },
		],
	},
	{
		id: "personal_tecnico",
		name: "Personal Técnico",
		sector: "music",
		subcategories: [
			{ id: "direccion_produccion", name: "Dirección de producción" },
			{ id: "extras_bandas_tecnicas", name: "Personal técnico" },
			{ id: "personal_montaje", name: "Personal de montaje" },
			{ id: "runners", name: "Runners" },
			{ id: "proyecto_tecnico", name: "Proyecto técnico" },
		],
	},
	{
		id: "infraestructura_recinto",
		name: "Infraestructura del Recinto",
		sector: "music",
		subcategories: [
			{ id: "infraestructura_evento", name: "Infraestructura evento" },
			{ id: "vallado", name: "Vallado y cerramientos" },
			{ id: "sanitarios", name: "Sanitarios portátiles" },
		],
	},
	{
		id: "logistica_transporte",
		name: "Logística y Transporte",
		sector: "music",
		subcategories: [
			{ id: "transporte_equipos", name: "Transporte de equipos" },
			{ id: "vehiculos_produccion", name: "Vehículos de producción" },
			{ id: "combustible", name: "Combustible" },
		],
	},
	{
		id: "promocion_marketing",
		name: "Promoción y Marketing",
		sector: "music",
		subcategories: [
			{ id: "foto_video", name: "Fotografía y vídeo" },
			{ id: "publicidad_agencias", name: "Publicidad y agencias" },
			{ id: "redes_sociales", name: "Redes sociales" },
			{ id: "web_digital", name: "Web y digital" },
			{ id: "rrpp", name: "Relaciones públicas" },
			{ id: "impresion_grafica", name: "Impresión y material gráfico" },
		],
	},
	{
		id: "hosteleria_catering",
		name: "Hostelería y Catering",
		sector: "music",
		subcategories: [
			{ id: "bebidas", name: "Bebidas" },
			{ id: "restauracion", name: "Restauración y menús" },
			{ id: "suministros_alimentacion", name: "Suministros alimentación" },
		],
	},
	{
		id: "servicios_evento",
		name: "Servicios del Evento",
		sector: "music",
		subcategories: [
			{ id: "seguridad", name: "Seguridad" },
			{ id: "control_acceso", name: "Control de acceso y ticketing" },
			{ id: "limpieza", name: "Limpieza" },
			{ id: "asistencia_medica", name: "Asistencia médica" },
			{ id: "prevencion_incendios", name: "Prevención de incendios" },
		],
	},
	{
		id: "administracion_estructura",
		name: "Administración y Estructura",
		sector: "music",
		subcategories: [
			{ id: "administracion", name: "Administración" },
			{ id: "alquiler_suministros", name: "Alquiler y suministros oficina" },
			{ id: "seguros", name: "Seguros" },
			{ id: "telecomunicaciones", name: "Telecomunicaciones" },
			{ id: "software_herramientas", name: "Software y herramientas" },
			{ id: "parking", name: "Parking" },
			{ id: "merchandising", name: "Merchandising" },
		],
	},
	// ── Cinema categories ───────────────────────────────────────────
	{
		id: "desarrollo_derechos",
		name: "Desarrollo y Derechos",
		sector: "cinema",
		subcategories: [
			{ id: "guion", name: "Guión" },
			{ id: "derechos", name: "Derechos" },
		],
	},
	{
		id: "talento_artistico_atl",
		name: "Talento Artístico ATL",
		sector: "cinema",
		subcategories: [
			{ id: "director", name: "Director/a" },
			{ id: "actores", name: "Actores/Actrices" },
			{ id: "compositor", name: "Compositor/a" },
			{ id: "director_fotografia", name: "Director/a de fotografía" },
			{ id: "director_arte", name: "Director/a de arte" },
			{ id: "otros_creativos", name: "Otros creativos" },
		],
	},
	{
		id: "postproduccion",
		name: "Postproducción",
		sector: "cinema",
		subcategories: [
			{ id: "edicion_montaje", name: "Edición y montaje" },
			{ id: "sonido_post", name: "Sonido en post" },
			{ id: "laboratorios", name: "Laboratorios" },
		],
	},
	{
		id: "personal_btl",
		name: "Personal BTL",
		sector: "cinema",
		subcategories: [
			{ id: "equipo_tecnico", name: "Equipo técnico" },
			{ id: "figuracion", name: "Figuración" },
			{ id: "equipo_produccion", name: "Equipo producción" },
		],
	},
	{
		id: "logistica",
		name: "Logística",
		sector: "cinema",
		subcategories: [
			{ id: "transporte_material", name: "Transporte de material" },
			{ id: "transporte_equipo", name: "Transporte del equipo" },
		],
	},
	{
		id: "alojamiento",
		name: "Alojamiento",
		sector: "cinema",
		subcategories: [
			{ id: "alojamiento_equipo", name: "Alojamiento equipo" },
			{ id: "catering_rodaje", name: "Catering rodaje" },
		],
	},
	{
		id: "localizaciones",
		name: "Localizaciones",
		sector: "cinema",
		subcategories: [
			{ id: "localizaciones", name: "Localizaciones y permisos" },
			{ id: "maquinaria_alquiler", name: "Maquinaria y vehículos" },
		],
	},
	{
		id: "promocion_distribucion",
		name: "Promoción y Distribución",
		sector: "cinema",
		subcategories: [
			{ id: "distribucion", name: "Distribución" },
			{ id: "copias", name: "Copias" },
		],
	},
	{
		id: "seguros",
		name: "Seguros",
		sector: "cinema",
		subcategories: [
			{ id: "seguros_produccion", name: "Seguros de producción" },
		],
	},
	{
		id: "administracion",
		name: "Administración",
		sector: "cinema",
		subcategories: [
			{ id: "gastos_financieros", name: "Gastos financieros" },
			{ id: "gastos_generales", name: "Gastos generales empresa" },
		],
	},
	// ── Shared categories (merged subcategories) ────────────────────
	{
		id: "produccion_tecnica",
		name: "Producción Técnica",
		sector: "shared",
		subcategories: [
			// Music
			{ id: "sonido_pa", name: "Sonido y PA" },
			{ id: "iluminacion", name: "Iluminación" },
			{ id: "escenarios", name: "Escenarios" },
			{ id: "backline", name: "Backline" },
			{ id: "rigging", name: "Rigging" },
			{ id: "lonas_vinilos", name: "Lonas y vinilos" },
			{ id: "maquinaria_generadores", name: "Maquinaria y generadores" },
			{ id: "decoracion_escenografia", name: "Decoración y escenografía" },
			{ id: "material_produccion", name: "Material de producción" },
			// Cinema
			{ id: "camaras_equipos", name: "Cámaras y equipos" },
			{ id: "sonido", name: "Sonido directo" },
			{ id: "escenografia", name: "Escenografía y decorados" },
			{ id: "vestuario", name: "Vestuario y caracterización" },
			{ id: "efectos_especiales", name: "Efectos especiales" },
		],
	},
	{
		id: "contingencias",
		name: "Contingencias",
		sector: "shared",
		subcategories: [{ id: "imprevistos", name: "Imprevistos" }],
	},
];

// ── Lookup maps (lazy-initialized singletons) ───────────────────────

const categoryMap = new Map<string, TaxonomyCategory>();
for (const cat of TAXONOMY_CATEGORIES) {
	categoryMap.set(cat.id, cat);
}

// ── Public API ──────────────────────────────────────────────────────

const _categoryGroups: OptionGroup[] = (() => {
	const musicCats = TAXONOMY_CATEGORIES.filter(
		(c) => c.sector === "music" || c.sector === "shared",
	);
	const cinemaCats = TAXONOMY_CATEGORIES.filter((c) => c.sector === "cinema");
	return [
		{
			label: "Artes escénicas y musicales",
			options: musicCats.map((c) => ({ value: c.id, label: c.name })),
		},
		{
			label: "Producciones audiovisuales",
			options: cinemaCats.map((c) => ({ value: c.id, label: c.name })),
		},
	];
})();

export function getCategoryGroups(): OptionGroup[] {
	return _categoryGroups;
}

export function getCategoryOptions(): FilterOption[] {
	return TAXONOMY_CATEGORIES.map((c) => ({ value: c.id, label: c.name }));
}

export function getSubcategoryOptions(categoryId: string): FilterOption[] {
	const cat = categoryMap.get(categoryId);
	if (!cat) return [];
	return cat.subcategories.map((s) => ({ value: s.id, label: s.name }));
}

export function getCategoryName(categoryId: string): string | null {
	return categoryMap.get(categoryId)?.name ?? null;
}

export function getSubcategoryName(
	categoryId: string,
	subcategoryId: string,
): string | null {
	const cat = categoryMap.get(categoryId);
	if (!cat) return null;
	return cat.subcategories.find((s) => s.id === subcategoryId)?.name ?? null;
}
